import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from './Modal.module.scss';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: 'default' | 'wide' | 'extra-wide';
  showCloseX?: boolean;
  children: React.ReactNode;
}

export default function Modal({ open, onClose, size = 'default', showCloseX = true, children }: ModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!open) return null;

  const sizeClass = size === 'wide' ? styles.wide : size === 'extra-wide' ? styles.extraWide : '';

  return ReactDOM.createPortal(
    <div className={`${styles.backdrop} ${styles.open} ${sizeClass}`} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        {showCloseX && (
          <button type="button" className={styles.closeX} onClick={onClose} aria-label="Close">
            &times;
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Modal title */
export function ModalTitle({ children }: { children: React.ReactNode }) {
  return <h2 className={styles.title}>{children}</h2>;
}

/** Modal subtitle/description */
export function ModalSubtitle({ children }: { children: React.ReactNode }) {
  return <p className={styles.subtitle}>{children}</p>;
}

/** Modal action buttons row */
export function ModalActions({ children }: { children: React.ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}
